import { Form as FormikForm, Formik, FormikErrors, FormikProps, FormikTouched } from 'formik';
import React, { useEffect, useState } from 'react';
import { Col, Row, Stack } from 'react-bootstrap';
import { To, useLocation, useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { BackButton } from '../../../../../components/buttons/BackButton';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import Stepper from '../../../../../components/stepper/Stepper';
import { useToast } from '../../../../../context/ToastContext';
import {
  useCreateExpenseMutation,
  useGetExpenseQuery,
  useUpdateExpenseMutation
} from '../../../../../services/endpoints/finance/expense';
import {
  expensesCategoriesOptions,
  expensesContextOptions,
  paymentMethodOptions
} from '../../../../../utils/consts/finance/finance';
import { getErrorMessage } from '../../../../../utils/utils';
import { FadeIn } from '../../../../animations/FadeIn';
import { ExpenseForm } from './ExpenseForm';

const steps = [{ name: 'Details', form: ExpenseForm }];

export interface FormValues {
  id?: number;
  course_id: { value: number | string; label: string };
  category: { value: string; label: string };
  amount: number;
  context: { value: string; label: string };
  due_on: string;
  paid_on?: string | null;
  notes: string;
  invoice_number: string;
  payment_method: { value: string; label: string };
  check_number?: number | null;
}

export interface StepProps {
  errors: Partial<FormikErrors<FormValues>>;
  touched: Partial<FormikTouched<FormValues>>;
  values?: FormValues;
  setFieldValue?: (field: string, value: any, shouldValidate?: boolean) => void;
  readOnly?: boolean;
}

interface AddEditExpenseProps {
  editing?: boolean;
}

export const AddEditExpense: React.FC<AddEditExpenseProps> = ({ editing }) => {
  const params = useParams();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const [currentStep, setCurrentStep] = useState(0);
  const { addToast } = useToast();
  const navigate = useNavigate();

  const [createExpense] = useCreateExpenseMutation();
  const [updateExpense] = useUpdateExpenseMutation();
  const { data, error, isLoading } = useGetExpenseQuery(Number(params.id!), {
    skip: !editing || !params.id
  });

  const [initialValues, setInitialValues] = useState<FormValues>({
    course_id: { value: urlParams.get('course_id') ?? '', label: '' },
    category: {
      value: urlParams.get('category') ?? '',
      label: expensesCategoriesOptions.find((c) => c.id === urlParams.get('category'))?.label || ''
    },
    amount: '' as any,
    context: {
      value: urlParams.get('context') ?? '',
      label: expensesContextOptions.find((c) => c.id === urlParams.get('context'))?.label || ''
    },
    due_on: '',
    notes: '',
    invoice_number: '',
    payment_method: { value: '', label: '' }
  });

  const validationSchemas = [
    Yup.object({
      course_id: Yup.object()
        .test('course-required', 'Session is required', (val: any) => !!val?.value)
        .required('Session is required'),
      category: Yup.object()
        .test('category-required', 'Category is required', (val: any) => !!val?.value)
        .required('Category is required'),
      amount: Yup.number()
        .transform((value, originalValue) => (originalValue === '' ? undefined : value))
        .typeError('Amount must be a number')
        .required('Amount is required')
        .max(9999.99, 'Amount must be less than $10,000'),
      context: Yup.object()
        .test('context-required', 'Context is required', (val: any) => !!val?.value)
        .required('Context is required'),
      due_on: Yup.string().nullable().optional(),
      paid_on: Yup.string().nullable().optional(),
      notes: Yup.string().nullable().optional(),
      invoice_number: Yup.string().optional().nullable(),
      payment_method: Yup.object()
        .test('payment-method-required', 'Payment method is required', (val: any) => !!val?.value)
        .required('Payment method is required'),
      check_number: Yup.number().nullable().optional()
    })
  ];

  useEffect(() => {
    if (editing && error && !isLoading) {
      addToast('Error while loading expense', 'error');
    }
  }, [editing, error, isLoading]);

  useEffect(() => {
    if (data && data.expense) {
      const { expense } = data;
      setInitialValues({
        id: expense.id,
        course_id: { value: expense.course?.id ?? '', label: expense.course?.title ?? '' },
        category: {
          value: expense.category,
          label: expensesCategoriesOptions.find((c) => c.id === expense.category)?.label || ''
        },
        amount: expense.amount,
        context: {
          value: expense.context,
          label: expensesContextOptions.find((c) => c.id === expense.context)?.label || ''
        },
        due_on: expense.due_on,
        paid_on: expense.paid_on,
        notes: expense.notes,
        invoice_number: expense.invoice_number ?? '',
        payment_method: {
          value: expense.payment_method,
          label: paymentMethodOptions.find((c) => c.id === expense.payment_method)?.label || ''
        },
        check_number: expense.check_number
      });
    }
  }, [data]);

  const handleSubmit = async (formValue: FormValues) => {
    try {
      const expenseData = {
        ...formValue,
        course_id: Number(formValue.course_id.value),
        category: formValue.category.value,
        context: formValue.context.value,
        payment_method: formValue.payment_method.value,
        paid_on: formValue.paid_on ?? undefined,
        check_number: formValue.check_number ?? undefined
      };
      if (editing) {
        const res = await updateExpense(expenseData).unwrap();
        if (res && res.expense) {
          addToast(`Expense updated succesfully`, 'success');
          navigate(-1 as To);
        }
      } else {
        const res = await createExpense(expenseData).unwrap();
        if (res && res.expense) {
          navigate(-1 as To);
          addToast('Expense created succesfully', 'success');
        } else throw new Error('A problem happened while creating expense');
      }
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  const handleNext = async (
    validateForm: () => Promise<FormikErrors<FormValues>>,
    setTouched: (touched: FormikTouched<FormValues>, shouldValidate?: boolean) => void,
    values: FormValues
  ) => {
    const errors = await validateForm();
    if (Object.keys(errors).length === 0) {
      setCurrentStep(currentStep + 1);
    } else {
      setTouched({
        course_id: true as any,
        category: true as any,
        amount: true,
        context: true as any,
        due_on: true,
        paid_on: true,
        notes: true,
        invoice_number: true,
        payment_method: true as any,
        check_number: true
      });
    }
  };

  return (
    <FadeIn className="p-lg-4">
      <Stack
        gap={3}
        className="py-lg-0 px-4 py-2"
      >
        <div>
          <BackButton
            text={`Back to expense${editing ? ' details' : 's'}`}
            color="#2B4E64"
            url={
              editing ? `/admin/finance/expenses/${data?.expense.id}` : '/admin/finance/expenses'
            }
          />
          <h2
            className="d-none d-lg-block text-info fw-bold mb-0"
            style={{ fontSize: '2.25rem' }}
          >
            {editing ? 'Edit' : 'New'} Expense
          </h2>
          {steps.length > 1 && (
            <Stepper
              steps={steps}
              currentStep={currentStep}
              editing={editing === true}
              onStepClick={(step) => setCurrentStep(step)}
            />
          )}
        </div>

        <div
          className="p-lg-5 p-3"
          style={{
            boxShadow: '0 4px 8px -2px rgba(16, 24, 40, .1),  0 4px 4px 0 rgba(0, 0, 0, .25)',
            border: '1px solid #EBEBEB',
            borderRadius: 10
          }}
        >
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchemas[currentStep]}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({
              submitForm,
              isSubmitting,
              validateForm,
              touched,
              errors,
              setTouched,
              values,
              setFieldValue
            }: FormikProps<FormValues>) => (
              <FormikForm className="text-start">
                <div style={{ minHeight: '65vh' }}>
                  {currentStep < steps.length &&
                    React.createElement(steps[currentStep]?.form, {
                      errors,
                      touched,
                      values,
                      setFieldValue
                    })}
                </div>

                <Row className="justify-content-end mt-3">
                  <Col
                    xs={12}
                    lg={6}
                    className="d-flex justify-content-end gap-2"
                  >
                    <SimpleButton
                      disabled={isSubmitting}
                      type="button"
                      onClick={() => navigate(-1 as To)}
                      className="w-50 mt-2"
                      variant="outline-primary"
                    >
                      Cancel
                    </SimpleButton>
                    <SimpleButton
                      disabled={isSubmitting || (errors && Object.keys(errors).length > 0)}
                      type="button"
                      onClick={() => {
                        if (currentStep === steps.length - 1) {
                          submitForm();
                        } else {
                          handleNext(validateForm, setTouched, values);
                        }
                      }}
                      className="w-50 mt-2"
                      variant="primary"
                    >
                      {currentStep === steps.length - 1
                        ? editing
                          ? 'Update Expense'
                          : 'Create Expense'
                        : 'Continue'}
                    </SimpleButton>
                  </Col>
                </Row>
              </FormikForm>
            )}
          </Formik>
        </div>
      </Stack>
    </FadeIn>
  );
};
